import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { ConfirmationModal } from './ui/Notification';
import { 
  FiGrid, FiUsers, FiInbox, FiActivity, 
  FiSettings, FiLogOut, FiShield, FiMenu 
} from 'react-icons/fi';

export default function AdminLayout() {
  const { adminUser, adminLogout } = useAuth();
  const navigate = useNavigate();
  const [logoutModal, setLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await adminApi.logout(adminUser.admin_session_token);
    } catch (e) {
      console.error('Logout error:', e);
    }
    adminLogout();
    navigate('/admin/login');
  };

  const navItems = [
    { to: '/admin', icon: FiGrid, label: 'Dashboard', end: true },
    { to: '/admin/clients', icon: FiUsers, label: 'Clients' },
    { to: '/admin/leads', icon: FiInbox, label: 'All Leads' },
    { to: '/admin/logs', icon: FiActivity, label: 'System Logs' },
    { to: '/admin/settings', icon: FiSettings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex flex-col md:flex-row">
      <ConfirmationModal 
        isOpen={logoutModal}
        title="Confirm Sign Out"
        message="Are you sure you want to end your administrative session?"
        confirmText="Sign Out"
        confirmVariant="danger"
        loading={loggingOut}
        onConfirm={handleLogout}
        onCancel={() => setLogoutModal(false)}
      />

      {/* Sidebar */}
      <aside className="w-full md:w-64 bg-navy-900 text-white flex flex-col shadow-2xl z-20">
        <div className="p-6 border-b border-white/10 flex items-center gap-3">
          <div className="w-8 h-8 bg-brand-cyan rounded-lg flex items-center justify-center text-navy-900 font-black text-sm">BPA</div>
          <div>
            <h1 className="font-black text-xs tracking-tighter uppercase">Admin Console</h1>
            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">BPA Solutions</p>
          </div>
        </div>

        <nav className="flex-grow p-4 space-y-1">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all
                ${isActive ? 'bg-brand-blue text-white shadow-lg' : 'text-slate-400 hover:bg-white/5 hover:text-white'}
              `}
            >
              <SafeIcon icon={item.icon} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-4 py-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-brand-cyan">
              <SafeIcon icon={FiShield} />
            </div>
            <div className="overflow-hidden">
              <p className="text-[10px] font-black text-white truncate">{adminUser?.email}</p>
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">{adminUser?.role}</p>
            </div>
          </div>
          <button 
            onClick={() => setLogoutModal(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 text-red-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-500/20 transition-all border border-red-500/20"
          >
            <SafeIcon icon={FiLogOut} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-grow p-4 md:p-8 lg:p-10 overflow-y-auto max-h-screen">
        <div className="max-w-6xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}